import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import styled from "styled-components";
import { Button } from "../../../../design-system";

// Shared modal for the Media Studio sections. Portals to body so it floats
// over the sticky header (z 1100). Escape and backdrop click close it.

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 7vh var(--space-4) var(--space-4);
`;

const Panel = styled.div<{ $width: number }>`
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: 100%;
  max-width: ${(p) => p.$width}px;
  max-height: 82vh;
  overflow-y: auto;
  padding: var(--space-4);
  border-radius: var(--radius-xl);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  box-shadow: var(--shadow-lg, 0 16px 48px rgba(0, 0, 0, 0.4));
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
`;

const Title = styled.div`
  font-family: var(--font-display);
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
`;

export type ModalProps = {
  title: React.ReactNode;
  onClose: () => void;
  width?: number;
  children: React.ReactNode;
};

// Modals can stack (a post detail over a stat drill-down). The stack makes
// Escape close only the top one, and the body scroll lock release only when
// the last modal goes.
const modalStack: symbol[] = [];

export const Modal: React.FC<ModalProps> = ({
  title,
  onClose,
  width = 720,
  children,
}) => {
  useEffect(() => {
    const id = Symbol("modal");
    modalStack.push(id);
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && modalStack[modalStack.length - 1] === id) {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      const i = modalStack.indexOf(id);
      if (i >= 0) modalStack.splice(i, 1);
      if (modalStack.length === 0) document.body.style.overflow = "";
    };
  }, [onClose]);

  // A modal opens under the cursor, so the trailing click of the gesture
  // that opened it (or an accidental double click) would land on whatever
  // button renders at that spot. Swallow clicks for the first beat.
  const mountedAtRef = useRef<number>(Date.now());

  return ReactDOM.createPortal(
    <Overlay
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onClickCapture={(e) => {
        if (Date.now() - mountedAtRef.current < 300) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Panel $width={width} role="dialog" aria-modal="true">
        <Head>
          <Title>{title}</Title>
          <Button variant="ghost" size="sm" onClick={onClose}>
            Close
          </Button>
        </Head>
        {children}
      </Panel>
    </Overlay>,
    document.body
  );
};

export default Modal;
